import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/ui-bits";

const PRIVACY = [
  {
    en: ["Introduction", "Stratedge UK (\"we\", \"our\") respects your privacy. This policy explains how we collect, use and protect personal data submitted through this website, including consultation requests, course registrations, course requests, contact inquiries and expert network applications."],
    ar: ["مقدمة", "تحترم ستراتيج المملكة المتحدة خصوصيتكم. توضح هذه السياسة كيفية جمعنا واستخدامنا وحمايتنا للبيانات الشخصية المقدمة عبر هذا الموقع، بما في ذلك طلبات الاستشارة والتسجيل في الدورات وطلبات الدورات واستفسارات التواصل وطلبات الانضمام إلى شبكة الخبراء."],
  },
  {
    en: ["Data We Collect", "We collect the information you provide directly: name, email, phone and WhatsApp numbers, country and city, organization and job title, professional and academic details, uploaded documents (CVs and photos), and the content of your messages."],
    ar: ["البيانات التي نجمعها", "نجمع المعلومات التي تقدمونها مباشرة: الاسم والبريد الإلكتروني وأرقام الهاتف والواتساب والدولة والمدينة والمنظمة والمسمى الوظيفي والتفاصيل المهنية والأكاديمية والمستندات المرفوعة (السير الذاتية والصور) ومحتوى رسائلكم."],
  },
  {
    en: ["How We Use Your Data", "We use your data to respond to inquiries, administer course registrations and payments, review expert applications, deliver services, and communicate with you about your requests. Expert applications remain private unless approved and published by our team; private contact details are never published without administrative approval."],
    ar: ["كيف نستخدم بياناتكم", "نستخدم بياناتكم للرد على الاستفسارات وإدارة التسجيلات والمدفوعات ومراجعة طلبات الخبراء وتقديم الخدمات والتواصل معكم بشأن طلباتكم. تبقى طلبات الخبراء خاصة ما لم تتم الموافقة عليها ونشرها من قبل فريقنا، ولا تُنشر بيانات التواصل الخاصة دون موافقة إدارية."],
  },
  {
    en: ["Storage & Security", "Data is stored in secured databases and object storage with access restricted to authorised Stratedge UK personnel. We retain personal data only as long as needed for the purposes described above."],
    ar: ["التخزين والأمان", "تُخزن البيانات في قواعد بيانات ومخازن ملفات مؤمنة مع تقييد الوصول إلى موظفي ستراتيج المخولين فقط. نحتفظ بالبيانات الشخصية فقط للمدة اللازمة للأغراض الموضحة أعلاه."],
  },
  {
    en: ["Your Rights", "You may request access, correction or deletion of your personal data at any time by writing to info@stratedgeuk.com. By submitting a form on this website you consent to the processing described in this policy."],
    ar: ["حقوقكم", "يمكنكم طلب الوصول إلى بياناتكم الشخصية أو تصحيحها أو حذفها في أي وقت عبر info@stratedgeuk.com. بإرسالكم أي نموذج على هذا الموقع فإنكم توافقون على المعالجة الموضحة في هذه السياسة."],
  },
];

const TERMS = [
  {
    en: ["About These Terms", "These terms govern the use of the Stratedge UK website and the purchase of consulting and training services. By using this website you accept these terms."],
    ar: ["حول هذه الشروط", "تحكم هذه الشروط استخدام موقع ستراتيج المملكة المتحدة وشراء خدمات الاستشارات والتدريب. باستخدامكم هذا الموقع فإنكم تقبلون هذه الشروط."],
  },
  {
    en: ["Services & Bookings", "Course registrations are confirmed upon receipt of payment in accordance with the payment instructions provided after booking. Stratedge UK may reschedule or cancel a course with reasonable notice; in such cases registered participants receive a full refund or an alternative date."],
    ar: ["الخدمات والحجوزات", "تُؤكد التسجيلات في الدورات عند استلام الدفع وفقاً لتعليمات الدفع المقدمة بعد الحجز. يجوز لستراتيج إعادة جدولة أو إلغاء دورة مع إشعار معقول، وفي هذه الحالات يحصل المشاركون على استرداد كامل أو موعد بديل."],
  },
  {
    en: ["Intellectual Property", "All content on this website — including text, design, logos, course materials and articles — is the property of Stratedge UK and may not be reproduced without written permission."],
    ar: ["الملكية الفكرية", "جميع محتويات هذا الموقع — بما فيها النصوص والتصميم والشعارات والمواد التدريبية والمقالات — ملك لستراتيج المملكة المتحدة ولا يجوز إعادة إنتاجها دون إذن كتابي."],
  },
  {
    en: ["Liability", "The information on this website is provided in good faith for general guidance and does not constitute professional advice for any specific situation. Stratedge UK's liability for services is limited to the fees paid for the relevant engagement."],
    ar: ["المسؤولية", "تُقدم المعلومات على هذا الموقع بحسن نية لأغراض الإرشاد العام ولا تشكل استشارة مهنية لحالة محددة. وتقتصر مسؤولية ستراتيج عن الخدمات على الرسوم المدفوعة للمشروع المعني."],
  },
  {
    en: ["Governing Law", "These terms are governed by the laws of England and Wales. Questions may be directed to info@stratedgeuk.com."],
    ar: ["القانون الواجب التطبيق", "تخضع هذه الشروط لقوانين إنجلترا وويلز. يمكن توجيه الاستفسارات إلى info@stratedgeuk.com."],
  },
];

function LegalDocument({ titleKey, sections, testid }) {
  const { t, lang } = useLanguage();
  return (
    <div data-testid={testid}>
      <section className="bg-navy-950 py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <h1 className="font-display text-4xl font-semibold text-white sm:text-5xl">{t(titleKey)}</h1>
            <p className="mt-3 text-xs uppercase tracking-widest text-slate-400">{t("legal.updated")}</p>
          </Reveal>
        </div>
      </section>
      <section className="py-16">
        <div className="mx-auto max-w-4xl space-y-10 px-4 sm:px-6">
          {sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <h2 className="font-display text-2xl font-semibold text-navy">{s[lang][0]}</h2>
              <p className="mt-3 leading-relaxed text-slate-600">{s[lang][1]}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PrivacyPage() {
  return <LegalDocument titleKey="legal.privacyTitle" sections={PRIVACY} testid="privacy-page" />;
}

export function TermsPage() {
  return <LegalDocument titleKey="legal.termsTitle" sections={TERMS} testid="terms-page" />;
}
